import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  Circle,
  CheckCircle,
  Delete,
  DoneAll,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiClient from '../lib/axios';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  entityType?: string;
  entityId?: string;
  createdAt: string;
}

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveNotification, setResolveNotification] = useState<Notification | null>(null);
  const [resolveStatus, setResolveStatus] = useState<'DONE' | 'REJECTED'>('DONE');
  const [resolveNote, setResolveNote] = useState('');

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const { data } = await apiClient.get<Notification[]>('/notifications');
      return data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await apiClient.post(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/notifications/read-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await apiClient.delete(`/notifications/${notificationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async ({ entityId, status, adminNote }: { entityId: string; status: string; adminNote?: string }) => {
      await apiClient.patch(`/medication-requests/${entityId}/resolve`, {
        status,
        adminNote: adminNote || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setResolveDialogOpen(false);
      setResolveNotification(null);
      setResolveNote('');
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.id);
    }

    // Open resolve dialog for medication requests
    if (notification.type === 'MEDICATION_REQUEST' && notification.entityId) {
      setResolveNotification(notification);
      setResolveStatus('DONE');
      setResolveNote('');
      setResolveDialogOpen(true);
      handleClose();
      return;
    }

    // Navigate to related entity if applicable
    if (notification.entityType === 'Order' && notification.entityId) {
      const role = localStorage.getItem('userRole');
      if (role === 'DOCTOR') {
        navigate(`/doctor/orders/${notification.entityId}`);
      }
    }

    handleClose();
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleDeleteNotification = (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNotificationMutation.mutate(notificationId);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount} color="error">
          {unreadCount > 0 ? <NotificationsActive /> : <Notifications />}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 360,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAll />}
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadMutation.isPending}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', width: '100%' }}>
              No notifications
            </Typography>
          </MenuItem>
        ) : (
          notifications.slice(0, 10).map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                '&:hover': {
                  backgroundColor: notification.isRead ? 'action.hover' : 'action.selected',
                },
                whiteSpace: 'normal',
                py: 1.5,
              }}
            >
              <ListItemIcon>
                {notification.isRead ? (
                  <CheckCircle fontSize="small" color="disabled" />
                ) : (
                  <Circle fontSize="small" color="primary" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={notification.title}
                secondary={
                  <>
                    <Typography variant="body2" color="text.secondary" component="span" display="block">
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span" display="block">
                      {formatTimeAgo(notification.createdAt)}
                    </Typography>
                  </>
                }
                primaryTypographyProps={{
                  fontWeight: notification.isRead ? 'normal' : 'bold',
                  fontSize: '0.875rem',
                }}
                secondaryTypographyProps={{
                  component: 'div',
                }}
              />
              <IconButton
                size="small"
                onClick={(e) => handleDeleteNotification(notification.id, e)}
                sx={{ ml: 1 }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}

        {notifications.length > 10 && (
          <>
            <Divider />
            <MenuItem onClick={handleClose}>
              <Typography variant="body2" color="primary" sx={{ textAlign: 'center', width: '100%' }}>
                View all notifications
              </Typography>
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
          Responder Solicitud
        </DialogTitle>
        <DialogContent>
          {resolveNotification && (
            <Typography sx={{ fontSize: 13, color: '#657786', mb: 2 }}>
              {resolveNotification.message}
            </Typography>
          )}
          <ToggleButtonGroup
            value={resolveStatus}
            exclusive
            onChange={(_, val) => val && setResolveStatus(val)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="DONE" sx={{ textTransform: 'none', fontSize: 13 }}>
              Completado
            </ToggleButton>
            <ToggleButton value="REJECTED" sx={{ textTransform: 'none', fontSize: 13 }}>
              Rechazado
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            fullWidth
            label="Nota (opcional)"
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            size="small"
            multiline
            rows={2}
            placeholder="Respuesta al doctor..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setResolveDialogOpen(false)}
            sx={{ textTransform: 'none', color: '#657786' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (resolveNotification?.entityId) {
                resolveMutation.mutate({
                  entityId: resolveNotification.entityId,
                  status: resolveStatus,
                  adminNote: resolveNote,
                });
              }
            }}
            variant="contained"
            disabled={resolveMutation.isPending}
            sx={{
              textTransform: 'none',
              background: resolveStatus === 'DONE' ? '#00A86B' : '#E53935',
              '&:hover': {
                background: resolveStatus === 'DONE' ? '#007F50' : '#C62828',
              },
            }}
          >
            {resolveStatus === 'DONE' ? 'Completar' : 'Rechazar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
